/// <amd-module name="Wit/Common/WorkItemDialogs/Components/EmailWorkItemsDialog" />
import { IMailConfigurationService } from "Notifications/Common/Services/MailConfigurationService";
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { ContentSize } from "VSSUI/Callout";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { IdentityPicker, IIdentity } from "VSSUI/IdentityPicker";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { convertValueToDisplayString } from "Wit/Common/QueryUtils/QueryUtils";
import { getWorkItemTrackingClient } from "Wit/Common/WorkItemDialogs/clients/WorkItemTracking";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";

const MaxNoteCharacters = 1024;
const MaxWorkItems = 200;

export interface IEmailWorkItemsDialogProps {
    columnFieldNames: string[];
    referenceFieldNames: string[];
    defaultSubject?: string;
    getTempQueryId?: () => Promise<string>;
    isQueryDirty?: boolean;
    loading?: boolean;
    onDismiss: () => void;
    queryId?: string;
    selectedItems: INamedFieldDataDictionary[];
    sortFields?: string[];
    tempQueryId?: string;
    wiql?: string;
    workItemProjectId?: string;
}

export class EmailWorkItemsDialog extends VssComponent<IEmailWorkItemsDialogProps> {
    private pickerProvider: IPeoplePickerProvider;
    private selectedIdentities = new ObservableArray<IIdentity>([]);
    private note = new ObservableValue("");
    private subject = new ObservableValue("");
    private sending = new ObservableValue(false);
    private errorMessage = new ObservableValue("");
    private showConfirmationDialog = new ObservableValue(false);
    private workItemService: IWorkItemService;
    private projectId: string;
    private isMailEnabled: boolean;

    public constructor(props: IEmailWorkItemsDialogProps, context: IVssComponentContext) {
        super(props, context);

        this.workItemService = context.pageContext.getService<IWorkItemService>("work-item-service");
        this.pickerProvider = new PeoplePickerProvider(this.context.pageContext, {
            identityTypes: ["user"]
        });
        if (this.props.defaultSubject) {
            this.subject.value = this.props.defaultSubject;
        }
        const projectId = getTFSPageData(this.context.pageContext)?.project?.id ?? this.props?.workItemProjectId;

        //Handles the case when we are doing an organization level search. We need to set workitem's projectId.
        if (!projectId) {
            throw new Error(Resources.NoProjectSpecifiedMessage);
        } else {
            this.projectId = projectId;
        }

        const mailConfigurationService = this.context.pageContext.getService<IMailConfigurationService>("IMailConfigurationService");
        this.isMailEnabled = mailConfigurationService.isEnabled();
    }

    public render() {
        if (this.props.isQueryDirty || !this.isMailEnabled) {
            return (
                <Dialog
                    titleProps={{ text: Resources.ErrorSendingEmail }}
                    footerButtonProps={[{ text: Resources.OK, primary: true, onClick: this.props.onDismiss }]}
                    onDismiss={this.onDismiss}
                >
                    {!this.isMailEnabled ? Resources.SendMailNotEnabled : Resources.UnsavedQueryMessage}
                </Dialog>
            );
        }
        return (
            <>
                <Observer subject={this.subject} selectedIdentities={this.selectedIdentities} note={this.note} errorMessage={this.errorMessage}>
                    {({ errorMessage, subject, selectedIdentities }) => (
                        <Dialog
                            contentSize={ContentSize.ExtraLarge}
                            titleProps={{ text: Resources.SendWorkItems }}
                            onDismiss={this.onDismiss}
                            footerButtonProps={[
                                {
                                    text: Resources.Cancel,
                                    onClick: this.onDismiss
                                },
                                {
                                    text: Resources.Send,
                                    onClick: this.onSend,
                                    primary: true,
                                    disabled: !subject || selectedIdentities.length === 0 || this.note.value.length > MaxNoteCharacters
                                }
                            ]}
                            showCloseButton={true}
                        >
                            <div className="flex-grow flex-column rhythm-vertical-16">
                                {this.props.loading ? (
                                    <Spinner label={Resources.Loading} />
                                ) : this.props.selectedItems.length === 0 ? (
                                    <MessageCard severity={MessageCardSeverity.Error}>{Resources.NoWorkItems}</MessageCard>
                                ) : (
                                    <>
                                        {errorMessage && (
                                            <MessageCard onDismiss={() => (this.errorMessage.value = "")} severity={MessageCardSeverity.Error}>
                                                {errorMessage}
                                            </MessageCard>
                                        )}
                                        <FormItem label={Resources.To}>
                                            <IdentityPicker
                                                className="email-dialog-item"
                                                onIdentityAdded={this.onIdentityAdded}
                                                onIdentitiesRemoved={this.onIdentitiesRemoved}
                                                onIdentityRemoved={this.onIdentityRemoved}
                                                pickerProvider={this.pickerProvider}
                                                selectedIdentities={this.selectedIdentities}
                                            />
                                        </FormItem>
                                        <FormItem label={Resources.Subject}>
                                            <TextField
                                                containerClassName="email-dialog-item"
                                                value={this.subject}
                                                onChange={(event, value: string) => {
                                                    this.subject.value = value;
                                                }}
                                            />
                                        </FormItem>
                                        <FormItem
                                            label={
                                                <div className="flex-row">
                                                    <span className="flex-grow email-dialog-label">{Resources.Note}</span>
                                                    <span className={css(this.note.value.length > MaxNoteCharacters && "character-count-error")}>
                                                        {format(Resources.CharacterCount, this.note.value.length, MaxNoteCharacters)}
                                                    </span>
                                                </div>
                                            }
                                        >
                                            <TextField
                                                containerClassName="email-dialog-item"
                                                multiline={true}
                                                value={this.note}
                                                onChange={(event, value: string) => {
                                                    this.note.value = value;
                                                }}
                                            />
                                        </FormItem>
                                        <div className="flex-column email-read-only-body">
                                            <span className="margin-bottom-16">{Resources.SelectedWorkItems}</span>
                                            {this.renderTable()}
                                        </div>
                                        <div className="flex-column">
                                            {this.props.selectedItems.length > MaxWorkItems && (
                                                <span>{format(Resources.EmailWorkItemsLimit, MaxWorkItems)}</span>
                                            )}
                                        </div>
                                        <Observer sending={this.sending}>
                                            {({ sending }: { sending: boolean }) => sending && <Spinner label={Resources.Sending} />}
                                        </Observer>
                                    </>
                                )}
                            </div>
                        </Dialog>
                    )}
                </Observer>

                <Observer showConfirmationDialog={this.showConfirmationDialog}>
                    {({ showConfirmationDialog }: { showConfirmationDialog: boolean }) =>
                        showConfirmationDialog && (
                            <Dialog
                                titleProps={{ text: Resources.UnsavedChanges }}
                                onDismiss={this.props.onDismiss}
                                lightDismiss={false}
                                modal={true}
                                footerButtonProps={[
                                    {
                                        text: Resources.Cancel,
                                        onClick: this.onConfirmationDismiss
                                    },
                                    {
                                        text: Resources.OK,
                                        onClick: this.props.onDismiss,
                                        primary: true
                                    }
                                ]}
                            >
                                {Resources.UnsavedChangesMessage}
                            </Dialog>
                        )
                    }
                </Observer>
            </>
        );
    }

    private renderTable = () => {
        // Use inline styles to match the table in the email.
        return (
            <table
                style={{
                    fontFamily: "Calibri, sans-serif",
                    borderCollapse: "collapse",
                    border: 0,
                    maxHeight: "180px",
                    width: "100%",
                    overflowY: 'auto',
                }}
                aria-label={Resources.SelectedWorkItems}
            >
                <tbody>
                    <tr style={{ background: "#106EBE", color: "white", verticalAlign: "top" }}>
                        {this.props.columnFieldNames.map((columnName: string) => (
                            <th
                                style={{
                                    border: "none",
                                    borderRight: "solid white 1.0pt",
                                    padding: "1.45pt .05in 1.45pt .05in",
                                    fontFamily: "Segoe UI, Helvetica Neue, Helvetica, Arial, Verdana",
                                    fontSize: "12px"
                                }}
                                key={columnName}
                            >
                                {columnName}
                            </th>
                        ))}
                    </tr>
                    {this.props.selectedItems
                        .filter(item => item[CoreFieldRefNames.WorkItemType]) // only show rows with loaded values
                        .map((selectedItem: INamedFieldDataDictionary, index: number) => (
                            <tr key={index} style={{ background: index % 2 === 0 ? "rgba(124, 124, 124, 0.5)" : "inherit", verticalAlign: "top" }}>
                                {this.props.referenceFieldNames.map((columnName: string) => (
                                    <td
                                        key={columnName}
                                        style={{
                                            border: "none",
                                            borderRight: "solid white 1.0pt",
                                            padding: "1.45pt .05in 1.45pt .05in",
                                            fontFamily: "Segoe UI, Helvetica Neue, Helvetica, Arial, Verdana",
                                            fontSize: "12px",
                                            maxWidth: "50px"
                                        }}
                                    >
                                        {columnName === CoreFieldRefNames.Id ? (
                                            <a
                                                href={this.workItemService.getWorkItemUrl(selectedItem[CoreFieldRefNames.Id], this.projectId)}
                                                target="_blank"
                                                tabIndex={0}
                                            >
                                                {selectedItem[columnName]}
                                            </a>
                                        ) : (
                                            convertValueToDisplayString(selectedItem[columnName])
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            </table>
        );
    };

    private onSend = async () => {
        this.sending.value = true;
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = vssPageService.getData();
        const currentUserId = pageData?.user.id || "";
        const client = getWorkItemTrackingClient(this.context.pageContext);
        const recipients = this.selectedIdentities.value.map(selectedIdentity => selectedIdentity.localId!);
        const projectId = this.projectId;
        let ids: number[] = [];
        if (!this.props.wiql) {
            ids = this.props.selectedItems.map(selectedItem => selectedItem[CoreFieldRefNames.Id]);
        }
        let tempQueryId = this.props.tempQueryId;
        if (!tempQueryId && this.props.getTempQueryId) {
            tempQueryId = await this.props.getTempQueryId();
        }

        try {
            await client.sendMail(
                {
                    message: {
                        body: this.note.value,
                        to: { tfIds: recipients, emailAddresses: [], unresolvedEntityIds: [] },
                        subject: this.subject.value,
                        cc: { tfIds: [currentUserId], emailAddresses: [], unresolvedEntityIds: [] },
                        replyTo: { tfIds: [currentUserId], emailAddresses: [], unresolvedEntityIds: [] },
                        inReplyTo: "",
                        messageId: ""
                    },
                    fields: this.props.referenceFieldNames,
                    wiql: this.props.wiql || "",
                    persistenceId: this.props.queryId || "",
                    ids: ids,
                    projectId: projectId,
                    sortFields: this.props.sortFields || [],
                    tempQueryId: tempQueryId || ""
                },
                projectId
            );
            this.props.onDismiss();
        } catch (ex) {
            this.errorMessage.value = ex.message ?? ex;
        } finally {
            this.sending.value = false;
        }
    };

    private onIdentityAdded = (identity: IIdentity) => {
        this.selectedIdentities.push(identity);
    };

    private onIdentityRemoved = (identity: IIdentity) => {
        this.selectedIdentities.value = this.selectedIdentities.value.filter((entity: IIdentity) => entity.entityId !== identity.entityId);
    };

    private onIdentitiesRemoved = (identities: IIdentity[]) => {
        this.selectedIdentities.value = this.selectedIdentities.value.filter(
            (entity: IIdentity) => identities.filter(item => item.entityId === entity.entityId).length === 0
        );
    };

    private onDismiss = () => {
        if ((this.subject.value && this.subject.value !== this.props.defaultSubject) || this.note.value) {
            this.showConfirmationDialog.value = true;
        } else {
            this.props.onDismiss();
        }
    };

    private onConfirmationDismiss = () => {
        this.showConfirmationDialog.value = false;
    };
}
